import {
  createImageObject,
  createOrganization,
  createPerson,
  createThing,
  createWebPage,
  makeAbsolute,
} from "@/components/schema/utils"
import { stripHtmlFromString } from "@/libs/helpers"

type NewsArticleSchemaProps = {
  title: string
  description: string
  articleBody?: string
  pathname: string
  category?: {
    name: string
    slug: { current: string | null }
    group?: { slug: { current: string | null } } | null
  }
  datePublished: string
  dateModified?: string
  authors?: readonly string[]
  imageUrl?: string | null
  tags?: Array<{ name: string }>
}

export const NewsArticleSchema = ({
  title,
  description,
  articleBody,
  pathname,
  category,
  datePublished,
  dateModified,
  authors,
  imageUrl,
  tags,
}: NewsArticleSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: title,
    description,
    articleBody: articleBody
      ? stripHtmlFromString(articleBody).split(" ").slice(0, 250).join(" ")
      : undefined,
    url: makeAbsolute(pathname),
    articleSection: category?.name,
    datePublished,
    dateModified: dateModified || datePublished,
    ...(authors?.length && {
      author: authors.map(name => createPerson({ name })),
    }),
    ...(category?.name && {
      isPartOf: {
        "@type": "CreativeWorkSeries",
        name: category.name,
        url: makeAbsolute(
          `/${category.group?.slug.current || ""}/${category.slug.current}/`,
        ),
      },
    }),
    publisher: createOrganization(),
    ...(imageUrl && { image: createImageObject(imageUrl) }),
    mainEntityOfPage: createWebPage(pathname),
    ...(tags?.length && {
      about: tags.map(({ name }) => createThing(name)),
    }),
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  )
}
